import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { addMonths, startOfMonth } from 'date-fns';
import { isNullish } from '@adornis/assert/isNullish.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Field } from '@adornis/baseql/decorators.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { writeFile } from 'fs/promises';
import iconv from 'iconv-lite';
import { calculateErfahrungsstufe } from '../../contracts/api/calculateErfahrungsstufe.js';
import {
  calculateWorkingHoursForEingruppierung,
  calculateWorkingHoursForVertrag,
} from '../../contracts/api/calculateWorkingHours.js';
import { isContractActive, SUPPLEMENT_RUNTIMES_SELECTION } from '../../contracts/api/contractTimeframes.js';
import type { Contract } from '../../contracts/entities/Contract.js';
import type { AGEingruppierung } from '../../contracts/entities/vertrag-sektionen/vertrag-eingruppierung/AGEingruppierung.js';
import type { KostenstelleProzentsatz } from '../../contracts/entities/vertrag-sektionen/vertrag-eingruppierung/KostenstelleProzentsatz.js';
import { getSalaryTable } from '../../salary-table/server/getSalaryTable.js';
import { ExpressEndpointFilenames, ExpressEndpointURLs } from '../../server/express-endpoints.js';
import type { LodasPersonData } from '../../server/lodas/LodasPersonData.js';
import { resolveGetAllLodasApplicableMitarbeiter } from '../../server/lodas/resolveGetAllLodasApplicableMitarbeiter.js';
import { generateSatzbeschreibung } from '../../server/lodas/satzbeschreibungGenerator.js';
import {
  convertToLodasFloatDataType,
  formatDatum,
  formatDatumMonth,
  map440,
  mapKennzeichnungArbeitgeber,
  mapKvStatusKurzfr,
  mapKzZuschlPvKinderlose,
  mapRentenVersicherungsNr,
  mapTaetigkeitsschluessel,
  mapVersicherungsStatus,
  mapVertragsArt,
  removeSpaceCharacter,
  transformRVBeitragsGruppe,
} from '../../server/lodas/transformers.js';
import { generateLodasTableFields, generateStammDaten } from '../../server/lodas/txt-helpers.js';
import type { LodasTableDaten } from '../../server/lodas/types.js';
import { Person } from '../../users/entities/person.js';
import { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';
import { EntityCollection } from '../enums/collection-names.js';
import {
  ULodPSDMitarbeiterTableEnum,
  ULodPsdArbeitszeitRegelmTableEnum,
  ULodPsdBeschaeftigungTableEnum,
  ULodPsdBesonderheitenTableEnum,
  ULodPsdFreibetragTableEnum,
  ULodPsdMABankTableEnum,
  ULodPsdPrivVersicherungTableEnum,
  ULodPsdSozialversicherungTableEnum,
  ULodPsdSteuerTableEnum,
  ULodPsdTaetigkeitTableEnum,
  ULodPsdVermoegensbildungTableEnum,
} from '../enums/lodas-tables-enums.js';
import { GermanInsuranceTypes, PauschalbesteuerungMiniJob } from '../enums/misc-enums.js';
import { generateChildData } from './generateChildData.js';
import { generateFehlzeitenData } from './generateFehlzeitenData.js';
import { generateKostenStellen } from './generateKostenstellen.js';
import { generateKrankenkassenLodasData } from './generateKrankenkassen.js';
import { generateLohnartLodasData } from './generateLohnart.js';
import { generateSalary } from './generateSalary.js';
import { generateSonderzahlungen } from './generateSonderzahlungen.js';

const LODAS_CONTRACT_EINGRUPPIERUNG_SELECTIONSET = {
  agVertragsInfos: {
    taetigkeit: 1,
    taetigkeitsschluessel: 1,
    kostenstelleProzentsaetze: {
      kostenstelleID: 1,
      kostenstelle: {
        kostenstelleSuffix: 1,
        school: {
          kostenstellePrefix: 1,
        },
      },
      prozentsatz: 1,
    },
  },
  agArbeitszeit: {
    ferienEinarbeiten: 1,
    maxDeputat: 1,
    stunden: 1,
    zaehler: 1,
  },
  agLohnGehaltszahlungen: {
    fiktivBeginnStufe1: 1,
    tarifgruppe: 1,
    monatlicheZahlungen: {
      lohnart: 1,
      betrag: 1,
      monat: 1,
      jahr: 1,
    },
    entgeltart: 1,
  },
} as const satisfies BaseQLSelectionSet<AGEingruppierung>;

const LODAS_CONTRACT_SELECTIONSET = {
  createdAt: 1,
  agSozialVersicherung: {
    geringfuegigeBeschaeftigung: 1,
    beitragsgruppenschluesselKV: 1,
    beitragsgruppenschluesselRV: 1,
    beitragsgruppenschluesselAV: 1,
    beitragsgruppenschluesselPV: 1,
    applyMidijob: 1,
  },
  agInfos: {
    einstellungsdatum: 1,
    gekuendigtZum: 1,
    pauschalbesteuerungMiniJob: 1,
    anstellungsform: 1,
    unterzeichnungsdatum: 1,
  },
  agEingruppierung1: LODAS_CONTRACT_EINGRUPPIERUNG_SELECTIONSET,
  agEingruppierung2: LODAS_CONTRACT_EINGRUPPIERUNG_SELECTIONSET,
} as const satisfies BaseQLSelectionSet<Contract>;

const LODAS_SELECTIONSET = {
  email: 1,

  contracts: LODAS_CONTRACT_SELECTIONSET,
  // the export checks the active state of this one below, which reads the runtimes of the supplements
  activeContract: { ...LODAS_CONTRACT_SELECTIONSET, supplements: SUPPLEMENT_RUNTIMES_SELECTION },
  mitarbeiterDaten: {
    _id: 1,
    personalNr: 1,
    lodasPersonalNr: 1,

    anInfos: {
      familienstand: 1,
      geburtsName: 1,
      ausstellungsDatumArbeitserlaubnis: 1,

      hatKinder: 1,
      kinder: {
        _id: 1,
        vorname: 1,
        nachname: 1,
        geburtsdatum: 1,
      },
      elternschaftsnachweisID: 1,
    },

    anSchwerbehinderung: { istVorhanden: 1 },
    anSteuerInfos: {
      kinderFreibetrag: 1,
      steuerklasse: 1,
      steuerIdentNr: 1,

      konfessionPartner: 1,
      steuerklassePartner: 1,
      factor: 1,
      verfuegbarkeit: 1,
      steuerfreibetraege: {
        jahresFreibetrag: 1,
        jahresHinzurechnungsbetrag: 1,
        monatsFreibetrag: 1,
        monatsHinzurechnungsbetrag: 1,
      },
    },
    anSozialVersicherung: {
      rentenVersicherungsNr: 1,
      krankenkassenID: 1,
      krankenkassen: {
        betriebsnummer: 1,
        bezeichnung: 1,
      },
      versicherungsStatus: 1,
    },
    anVermoegenswirksameLeitungen: {
      vermoegensbildung: 1,
      vermoegensbildungArbeitgeberAnteil: 1,
      lastschrifteinzug: 1,
      iban: 1,
      vertragsnummer: 1,
      vertragsart: 1,
      isAltersvermoegenswirksameLeistung: 1,
      vertragBegin: 1,
      vertragEnd: 1,
      institut: 1,
    },
    anPrivatVersicherung: {
      istPrivatVersichert: 1,
      krankenBeitrag: 1,
      pflegenBeitrag: 1,
      beitragsanteilBasisKrankenversicherung: 1,
    },
    anBeschaeftigung: {
      personenGruppe: 1,
    },
    jobTicketBestellDatum: 1,
    jobTicketKuendigungsDatum: 1,
  },
  profilDaten: {
    bankInfos: { iban: 1, kontoinhaber: 1 },
    adresse: { city: 1, country: 1, zip: 1, streetAndNumber: 1, auslandsPlz: 1, attentionLine: 1 },
    stammdaten: {
      geburtsDatum: 1,
      nachname: 1,
      geschlecht: 1,
      telefonNr: 1,
      vorname: 1,
      namenszusatz: 1,
      geburtsLand: 1,
      geburtsOrt: 1,
      staatsangehoerigkeit: 1,
      konfession: 1,
    },
  },
  abwesenheiten: {
    personID: 1,
    start: 1,
    ende: 1,
  },
} as const satisfies BaseQLSelectionSet<Person>;

@Entity()
export class LodasConfiguration extends AdornisEntity {
  static override _class = 'LodasConfiguration';

  @Field(type => String, { default: v => v ?? 'stammdaten' })
  public type!: 'stammdaten' | 'bewegdaten';

  @Field(type => Boolean, { default: v => v ?? false })
  public steuerdaten!: boolean;

  @Field(type => Boolean, { default: v => v ?? true })
  public tariftabelle!: boolean;

  @Field(type => Boolean, { default: v => v ?? false })
  public krankenkassen!: boolean;
}

const getAllLodasApplicableMitarbeiter = registerQuery({
  type: () => [Person],
  operationName: 'getAllLodasApplicableMitarbeiter',
  middlewares: [checkPermissionMiddleware(Permission.LODAS_EXPORT, ANY_CONTEXT)],
  resolve: resolveGetAllLodasApplicableMitarbeiter,
  params: [
    { type: () => UserListSelectionInterface, name: 'listSelection' },
    { type: () => Boolean, name: 'count' },
    { type: () => Int, name: 'skip' },
  ],
});

/**
 * Get Existed PersonalNr from database
 * Maybe the 1 of the personalNumber
 * @returns existedPersonalNr[]
 */
async function getExistingLodasPersonalNumbers() {
  const personCol = await getRawCollection(EntityCollection.PERSON);
  const daten = await personCol
    .aggregate([
      {
        $project: {
          _id: 0,
          'mitarbeiterDaten.personalNr': 1,
          'mitarbeiterDaten.lodasPersonalNr': 1,
        },
      },
    ])
    .toArray();

  const listOfPersonalNr = new Set(
    daten.map(d => (d.mitarbeiterDaten?.personalNr ? parseInt(d.mitarbeiterDaten.personalNr as string) : 0)),
  );
  const listOfLodasPersonalNr = new Set(
    daten.map(d => (d.mitarbeiterDaten?.lodasPersonalNr ? parseInt(d.mitarbeiterDaten.lodasPersonalNr as string) : 0)),
  );
  return { personalNumbers: [...listOfPersonalNr], lodasPersonalNumbers: [...listOfLodasPersonalNr] };
}

function splitAddress(address: Maybe<string>): { street: string; housenumber: string } {
  if (!address) return { street: '', housenumber: '' };

  let previousWasDigit = false;
  let lastDigitBeginning = -1;
  for (let i = 0; i < address.length; i++) {
    if (/\d/.test(address[i]!) || (previousWasDigit && !/\s/.test(address[i]!))) {
      if (previousWasDigit) continue;

      previousWasDigit = true;
      lastDigitBeginning = i;
    } else {
      previousWasDigit = false;
    }
  }

  if (lastDigitBeginning === -1) {
    // No digit found, return entire string as street and empty housenumber
    return { street: address.trim(), housenumber: '' };
  }

  const street = address.slice(0, lastDigitBeginning).trim();
  const housenumber = address.slice(lastDigitBeginning).trim();

  return { street, housenumber };
}

function splitTarifGruppe(gruppe: Maybe<string>): { tarifGruppe: string; tarifGruppeZusatz: string } {
  if (!gruppe) return { tarifGruppe: '', tarifGruppeZusatz: '' };
  const realGroup = gruppe.slice(1);

  const match = realGroup.match(/^(\d+)(\w*)$/);
  if (!match) {
    return { tarifGruppe: '', tarifGruppeZusatz: '' };
  }
  const [_, number, suffix] = match;
  return { tarifGruppe: number ?? '', tarifGruppeZusatz: suffix ?? '' };
}

function convertMartinusPersonToLodasPersonData(persons: Person[]): LodasPersonData[] {
  // das Jobticket gilt immer für ganze Monate, die Grenzen liegen also auf dem Kalendertags-Raster
  const today = todayAsCalendarDay();
  const firstOfMonth = startOfMonth(today);
  const startOfNextMonth = startOfMonth(addMonths(today, 1));

  const personenDaten = persons.map(person => {
    const { mitarbeiterDaten, profilDaten, email } = person;

    const activeContract = person.activeContract;

    if (!activeContract) return;

    const { privat, pflichtVersichert, freiwilligVersichert } = mapVersicherungsStatus(
      mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus ?? GermanInsuranceTypes.DEFAULT,
      mitarbeiterDaten?.anSozialVersicherung?.krankenkassen?.bezeichnung ?? '',
    );

    const mappedKvStatusKurzfr = mapKvStatusKurzfr(
      mitarbeiterDaten?.anGeringfuegigeBeschaeftigung?.versicherungsstatusKurzfristigBeschaeftigt,
    );

    const { ausgTaetigkeit, schulabschluss, ausbildungsabschluss, arbeitnehmerueberlassung, vertragsform } =
      mapTaetigkeitsschluessel(activeContract?.agEingruppierung1?.agVertragsInfos?.taetigkeitsschluessel);

    const gvGfSchluessel = activeContract?.agSozialVersicherung?.geringfuegigeBeschaeftigung;

    const { street, housenumber } = splitAddress(profilDaten?.adresse?.streetAndNumber);

    const tarifInfo1 = calculateErfahrungsstufe(activeContract, '1');
    const tarifInfo2 = calculateErfahrungsstufe(activeContract, '2');

    // const { tarifGruppe, tarifGruppeZusatz } = splitTarifGruppe(
    //   latestVertrag?.agEingruppierung1?.agLohnGehaltszahlungen?.tarifgruppe,
    // );

    const stripZero = (number: Maybe<number>) => {
      if (isNullish(number)) return '';
      if (number > 0) return number;
      return '';
    };

    // lodas specifc validation. We do this here because the data is only needed now in its entirety and some things are too complex to let the employee himself fill these. It needs to be done by an psb
    // another thing is the validation across complex joined data
    if (
      activeContract.agSozialVersicherung?.beitragsgruppenschluesselKV === 9 &&
      mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus !== GermanInsuranceTypes.VOLUNTARY
    )
      throw new ExpectedError(
        'Person ' +
          profilDaten?.stammdaten?.vorname?.trim() +
          ' ' +
          profilDaten?.stammdaten?.nachname?.trim() +
          ' needs to be voluntarily insured if KV type 9 is active',
      );

    if (
      mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus === GermanInsuranceTypes.VOLUNTARY &&
      activeContract.agSozialVersicherung?.beitragsgruppenschluesselKV !== 9 &&
      activeContract.agSozialVersicherung?.beitragsgruppenschluesselKV !== 0
    )
      throw new ExpectedError(
        'Person ' +
          profilDaten?.stammdaten?.vorname?.trim() +
          ' ' +
          profilDaten?.stammdaten?.nachname?.trim() +
          ' needs to be KV type 9 if voluntarily insured',
      );

    const calcWochen = (wochenarbeitszeit: Maybe<number>) => {
      if (isNullish(wochenarbeitszeit))
        return {
          regelm_az_mo: '0,00',
          regelm_az_di: '0,00',
          regelm_az_mi: '0,00',
          regelm_az_do: '0,00',
          regelm_az_fr: '0,00',
        };
      const perDay = parseFloat((wochenarbeitszeit / 5).toFixed(2));
      const friday = wochenarbeitszeit - perDay * 4;

      return {
        regelm_az_mo: perDay.toFixed(2).replaceAll('.', ','),
        regelm_az_di: perDay.toFixed(2).replaceAll('.', ','),
        regelm_az_mi: perDay.toFixed(2).replaceAll('.', ','),
        regelm_az_do: perDay.toFixed(2).replaceAll('.', ','),
        regelm_az_fr: friday.toFixed(2).replaceAll('.', ','),
      };
    };

    const weekWorkTime = calculateWorkingHoursForVertrag(activeContract);
    const workTimeAg1 = activeContract.agEingruppierung1
      ? calculateWorkingHoursForEingruppierung(activeContract.agEingruppierung1)
      : 0;
    const workTimeAg2 = activeContract.agEingruppierung2
      ? calculateWorkingHoursForEingruppierung(activeContract.agEingruppierung2)
      : 0;

    const buildKostenStelle = (k: KostenstelleProzentsatz | undefined) => {
      if (!k) throw new Error('No Kostenstelle!');

      const prefix = k.kostenstelle?.school?.kostenstellePrefix ?? '';
      let suffix = k.kostenstelle?.kostenstelleSuffix ?? '';
      if (suffix?.length === 1) suffix = `0${suffix}`;
      return prefix + suffix;
    };

    const isIndividuell =
      !activeContract?.agInfos?.pauschalbesteuerungMiniJob ||
      activeContract.agInfos.pauschalbesteuerungMiniJob === PauschalbesteuerungMiniJob.INDIVIDUELL;

    return {
      mitarbeiterID: person?._id,
      personalNr: mitarbeiterDaten?.personalNr ?? '', // u lod psd mitarbeiter table
      email: email ?? '',
      vorname: profilDaten?.stammdaten?.vorname?.replaceAll('(', '').replaceAll(')', '').trim() ?? '',
      nachname: profilDaten?.stammdaten?.nachname?.replaceAll('(', '').replaceAll(')', '').trim() ?? '',
      street: street?.trim(),
      housenumber: housenumber?.trim(),
      zip: profilDaten?.adresse?.zip ?? '',
      city: profilDaten?.adresse?.city ?? '',
      country: profilDaten?.adresse?.country ?? '',
      staatsangehoerigkeit: person.profilDaten?.stammdaten?.staatsangehoerigkeit ?? '',
      geburtsDatum: profilDaten?.stammdaten?.geburtsDatum ?? '',
      geburtsName: mitarbeiterDaten?.anInfos?.geburtsName?.trim() ?? '',
      geburtsOrt: person.profilDaten?.stammdaten?.geburtsOrt?.trim() ?? '',
      geburtsLand: person.profilDaten?.stammdaten?.geburtsLand ?? '',
      geschlecht: profilDaten?.stammdaten?.geschlecht ?? '',
      familienstand: mitarbeiterDaten?.anInfos?.familienstand ?? '',
      rentenVersicherungsNr: mitarbeiterDaten?.anSozialVersicherung?.rentenVersicherungsNr
        ? mapRentenVersicherungsNr(mitarbeiterDaten.anSozialVersicherung.rentenVersicherungsNr)
        : '',
      istVorhanden: map440(mitarbeiterDaten?.anSchwerbehinderung?.istVorhanden),
      lodasPersonalNr: mitarbeiterDaten?.lodasPersonalNr ?? '',
      arbeitserlaubnis: formatDatum(mitarbeiterDaten?.anInfos?.ausstellungsDatumArbeitserlaubnis),
      namenszusatz: profilDaten?.stammdaten?.namenszusatz ?? '',
      nazu_gebname: profilDaten?.stammdaten?.namenszusatz ?? '',
      auslandsPlz: profilDaten?.adresse?.auslandsPlz ?? '',
      telefonNr: profilDaten?.stammdaten?.telefonNr?.toString() ?? '',
      iban: removeSpaceCharacter(profilDaten?.bankInfos?.iban),
      kontoinhaber: profilDaten?.bankInfos?.kontoinhaber ?? '',
      steuerklasse: isIndividuell ? (mitarbeiterDaten?.anSteuerInfos?.steuerklasse ?? '') : '',
      steuerIdentNr: removeSpaceCharacter(mitarbeiterDaten?.anSteuerInfos?.steuerIdentNr),
      kinderFreibetrag: isIndividuell
        ? convertToLodasFloatDataType(mitarbeiterDaten?.anSteuerInfos?.kinderFreibetrag ?? 0.0)
        : '',
      konfession: isIndividuell ? (profilDaten?.stammdaten?.konfession ?? '') : '',
      konfessionPartner: isIndividuell ? (mitarbeiterDaten?.anSteuerInfos?.konfessionPartner ?? '') : '',
      zahlungsart: '5', // SEPA-Überweisung
      gesetzlichKrankenkasse: pflichtVersichert,
      freiwilligKrankenkasse: freiwilligVersichert,
      privatKrankenversichert: privat,
      ausgTaetigkeit,
      schulabschluss,
      ausbildungsabschluss,
      arbeitnehmerueberlassung,
      vertragsform,
      // datumUrsprBefr: mitarbeiterDaten?.datumUrsprBefr ?? '', // u_lod_psd_beschaeftigung table
      azWtlIndiv: weekWorkTime.toFixed(2).replaceAll('.', ','),
      ...calcWochen(weekWorkTime),
      gvGfSchluessel: gvGfSchluessel ?? '',
      // factor: // TODO data in db is garbage, so its temporary disabled
      //   mitarbeiterDaten?.anSteuerInfos?.steuerklasse && mitarbeiterDaten?.anSteuerInfos?.factor
      //     ? mapFaktor(mitarbeiterDaten?.anSteuerInfos?.steuerklasse, mitarbeiterDaten?.anSteuerInfos?.factor)
      //     : '',
      jahresFreibetrag: mitarbeiterDaten?.anSteuerInfos?.steuerfreibetraege?.jahresFreibetrag?.toString() ?? '',
      jahresHinzurechnungsbetrag:
        mitarbeiterDaten?.anSteuerInfos?.steuerfreibetraege?.jahresHinzurechnungsbetrag?.toString() ?? '',
      monatsFreibetrag: mitarbeiterDaten?.anSteuerInfos?.steuerfreibetraege?.monatsFreibetrag?.toString() ?? '',
      monatsHinzurechnungsbetrag:
        mitarbeiterDaten?.anSteuerInfos?.steuerfreibetraege?.monatsHinzurechnungsbetrag?.toString() ?? '',
      kzZuschlPvKinderlose: mapKzZuschlPvKinderlose(mitarbeiterDaten?.anInfos),
      kvStatusKurzfr: mappedKvStatusKurzfr,
      adresseAnschriftenzusatz: profilDaten?.adresse?.attentionLine ?? '',
      eintrittdatum: activeContract.agInfos?.einstellungsdatum
        ? formatDatum(activeContract.agInfos.einstellungsdatum)
        : '',
      krankenversicherungType: activeContract.agSozialVersicherung?.beitragsgruppenschluesselKV?.toString() ?? '',
      rentenversicherungType: activeContract.agSozialVersicherung?.beitragsgruppenschluesselRV?.toString() ?? '',
      arbeitslosenversicherungType: activeContract.agSozialVersicherung?.beitragsgruppenschluesselAV?.toString() ?? '',
      pflegeversicherungType: activeContract.agSozialVersicherung?.beitragsgruppenschluesselPV?.toString() ?? '',
      applyMidijob: map440(activeContract.agSozialVersicherung?.applyMidijob),
      vermoegensbildung: convertToLodasFloatDataType(
        mitarbeiterDaten?.anVermoegenswirksameLeitungen?.vermoegensbildung,
      ),
      vermoegensbildungArbeitgeberAnteil: convertToLodasFloatDataType(
        mitarbeiterDaten?.anVermoegenswirksameLeitungen?.vermoegensbildungArbeitgeberAnteil,
      ),
      lastschrifteinzug: map440(mitarbeiterDaten?.anVermoegenswirksameLeitungen?.lastschrifteinzug),
      vwlIban: removeSpaceCharacter(mitarbeiterDaten?.anVermoegenswirksameLeitungen?.iban),
      vertragsnummer: mitarbeiterDaten?.anVermoegenswirksameLeitungen?.vertragsnummer?.trim() ?? '',
      vertragsart: mapVertragsArt(mitarbeiterDaten?.anVermoegenswirksameLeitungen?.vertragsart),
      isAltersvermoegenswirksameLeistung: map440(
        mitarbeiterDaten?.anVermoegenswirksameLeitungen?.isAltersvermoegenswirksameLeistung,
      ),
      vertragBegin: formatDatum(mitarbeiterDaten?.anVermoegenswirksameLeitungen?.vertragBegin),
      vertragEnd: formatDatum(mitarbeiterDaten?.anVermoegenswirksameLeitungen?.vertragEnd),
      privatPflegeversichert: privat ? '1' : '',
      gesamtbeitragKv: privat
        ? convertToLodasFloatDataType(stripZero(mitarbeiterDaten?.anPrivatVersicherung?.krankenBeitrag))
        : '',
      gesamtbeitragPv: privat
        ? convertToLodasFloatDataType(stripZero(mitarbeiterDaten?.anPrivatVersicherung?.pflegenBeitrag))
        : '',
      monatlBeitrantBasisKv: privat
        ? convertToLodasFloatDataType(mitarbeiterDaten?.anPrivatVersicherung?.beitragsanteilBasisKrankenversicherung)
        : '',
      institut: mitarbeiterDaten?.anVermoegenswirksameLeitungen?.institut?.trim() ?? '',
      umlSchluessel: '2',
      verfuegbarkeit: mapKennzeichnungArbeitgeber(mitarbeiterDaten?.anSteuerInfos?.verfuegbarkeit),
      tarifstufe: tarifInfo1?.stufe.toString() ?? '',
      tarifstufeBeginn: formatDatumMonth(tarifInfo1?.startOfPhase),
      // tarifgruppe: tarifGruppe,
      // tarifgruppeZusatz: tarifGruppeZusatz,
      personenGruppe: mitarbeiterDaten?.anBeschaeftigung?.personenGruppe?.toString() ?? '',
      // verwErsteintrElenaBn: '1',
      rvBeitragsGruppe: transformRVBeitragsGruppe(activeContract.agInfos?.anstellungsform),
      entlohnungsform: '2',
      vwlAgAnteil: mitarbeiterDaten?.anVermoegenswirksameLeitungen?.vertragsnummer?.trim() ? '402' : '',
      kostenStelleProzent: [
        ...(activeContract.agEingruppierung1?.agVertragsInfos?.kostenstelleProzentsaetze?.map(k =>
          k.prozentsatz?.toFixed(2).replaceAll('.', ','),
        ) ?? []),
        ...(activeContract.agEingruppierung2?.agVertragsInfos?.kostenstelleProzentsaetze?.map(k =>
          k.prozentsatz?.toFixed(2).replaceAll('.', ','),
        ) ?? []),
      ].filter(predicate => !!predicate),
      kostenStelle: [
        ...(activeContract.agEingruppierung1?.agVertragsInfos?.kostenstelleProzentsaetze?.map(buildKostenStelle) ?? []),
        ...(activeContract.agEingruppierung2?.agVertragsInfos?.kostenstelleProzentsaetze?.map(buildKostenStelle) ?? []),
      ],
      salaryInformation: [
        ...(activeContract?.agEingruppierung1
          ? [
              {
                tarifGruppe: activeContract?.agEingruppierung1?.agLohnGehaltszahlungen?.tarifgruppe ?? '',
                erfahrungstufe: tarifInfo1?.stufe ?? 1,
                woechentlicheArbeitszeit: calculateWorkingHoursForEingruppierung(activeContract.agEingruppierung1),
                entgeltart: activeContract.agEingruppierung1.agLohnGehaltszahlungen?.entgeltart,
                monthlyPay: activeContract?.agEingruppierung1.agLohnGehaltszahlungen?.monatlicheZahlungen
                  ?.filter(z => z.monat === undefined || z.monat === null)
                  ?.map(z => ({
                    lohnart: z.lohnart,
                    betrag: z.betrag,
                  })),
                jobticket:
                  !!mitarbeiterDaten?.jobTicketBestellDatum &&
                  mitarbeiterDaten?.jobTicketBestellDatum <= firstOfMonth &&
                  (!!!mitarbeiterDaten.jobTicketKuendigungsDatum ||
                    mitarbeiterDaten.jobTicketKuendigungsDatum >= startOfNextMonth),
              },
            ]
          : []),
        ...(activeContract?.agEingruppierung2
          ? [
              {
                tarifGruppe: activeContract?.agEingruppierung2?.agLohnGehaltszahlungen?.tarifgruppe ?? '',
                erfahrungstufe: tarifInfo2?.stufe ?? 1,
                woechentlicheArbeitszeit: calculateWorkingHoursForEingruppierung(activeContract.agEingruppierung2),
                entgeltart: activeContract.agEingruppierung2.agLohnGehaltszahlungen?.entgeltart,
                monthlyPay: activeContract?.agEingruppierung2.agLohnGehaltszahlungen?.monatlicheZahlungen
                  ?.filter(z => z.monat === undefined || z.monat === null)
                  ?.map(z => ({
                    lohnart: z.lohnart,
                    betrag: z.betrag,
                  })),
                jobticket: false,
              },
            ]
          : []),
      ],
      childInformation:
        mitarbeiterDaten?.anInfos?.kinder?.length && mitarbeiterDaten.anInfos.hatKinder
          ? mitarbeiterDaten.anInfos.kinder.map((child, index) => ({
              kindNr: index + 1,
              kindVorname: child.vorname ?? '',
              kindNachname: child.nachname ?? '',
              kindGeburtsdatum: formatDatum(child.geburtsdatum),
            }))
          : [],
      fehlzeiten:
        person.abwesenheiten
          ?.map(fehlzeit => ({
            start: fehlzeit.start!,
            end: fehlzeit.ende!,
            grund: fehlzeit._class,
          }))
          .sort((a, b) => (a.start! > b.start! ? 1 : -1)) ?? [],
      beschaeft_nr: '1',
      pauschalsteuer: isIndividuell ? '0' : '1',
      pauschalsteuerANKZ: !activeContract.agInfos?.pauschalbesteuerungMiniJob
        ? '0'
        : activeContract.agInfos.pauschalbesteuerungMiniJob === PauschalbesteuerungMiniJob.INDIVIDUELL
          ? ''
          : activeContract.agInfos.pauschalbesteuerungMiniJob === PauschalbesteuerungMiniJob.AG_ZAHLT
            ? '2'
            : '1',

      bewegungSonderzahlungen: [
        ...(activeContract.agEingruppierung1?.agLohnGehaltszahlungen?.monatlicheZahlungen
          ?.filter(z => z.monat !== null && z.monat !== undefined)
          .map(z => ({
            pnr: mitarbeiterDaten?.lodasPersonalNr ?? '',
            art: z.lohnart ?? '',
            betrag: workTimeAg1 ? (z.betrag ?? 0) * (workTimeAg1 / 39.0) : 0,
            kostenstelle: buildKostenStelle(
              activeContract?.agEingruppierung1?.agVertragsInfos?.kostenstelleProzentsaetze[0],
            ),
            month: z.monat,
            year: z.jahr,
          })) ?? []),
        ...(activeContract.agEingruppierung2?.agLohnGehaltszahlungen?.monatlicheZahlungen
          ?.filter(z => z.monat !== null && z.monat !== undefined)
          .map(z => ({
            pnr: mitarbeiterDaten?.lodasPersonalNr ?? '',
            art: z.lohnart ?? '',
            betrag: workTimeAg2 ? (z.betrag ?? 0) * (workTimeAg2 / 39.0) : 0,
            kostenstelle: buildKostenStelle(
              activeContract?.agEingruppierung2?.agVertragsInfos?.kostenstelleProzentsaetze[0],
            ),
            month: z.monat,
            year: z.jahr,
          })) ?? []),
      ],
    } satisfies LodasPersonData;
  });

  return personenDaten;
}

/**
 * Generate lodas mitarbeiter personal nummer in case martinus personal nummer is greater than 99999
 * @return Promise<PersonDaten[]>
 */
async function convertAndFixPersonalNUmberToLodasFormat(persons: LodasPersonData[]): Promise<LodasPersonData[]> {
  const personCol = await getRawCollection<EntityData<Person>>(EntityCollection.PERSON);
  const validLengthOfPersonalNr = 5;
  const maxAvailablePersonalNr = 99999;
  const { personalNumbers, lodasPersonalNumbers } = await getExistingLodasPersonalNumbers();
  const availableLodasNumbers = Array.from({ length: maxAvailablePersonalNr }, (_, i) => i + 1).filter(
    x => !lodasPersonalNumbers.includes(x) && x > 20,
  );

  persons.sort((a, b) => {
    return parseInt(a.personalNr) - parseInt(b.personalNr);
  });

  for (const person of persons) {
    if (!person.personalNr) {
      throw new Error('Person hat keine Personalnummer ' + person.vorname + ' ' + person.nachname);
    }

    // if the person already has a loadas personal number we skip it
    if (person.lodasPersonalNr) continue;

    let newNumber: string = '';

    // if the person has a fitting personal number we just use it, because it should already be unique
    if (person.personalNr.length <= validLengthOfPersonalNr) {
      newNumber = person.personalNr;
    } else if (person.personalNr.length === validLengthOfPersonalNr + 1) {
      // uwe specific: if the number is just one to long we cut the first number and check if it already exists. If no we just use this cut number
      newNumber = person.personalNr.slice(1).replace(/^0+/, '');
    }

    // check if the new number is available. if yes take it if, if now get to fallback
    const index = availableLodasNumbers.findIndex(v => v === parseInt(newNumber));
    if (index !== -1) {
      availableLodasNumbers.splice(index, 1);
    } else {
      newNumber = '';
    }

    // fallback to on the fly generated values
    if (!newNumber) {
      let currentLodasPersonalNr: number | undefined = 0;
      let index = 0;
      // check if the number would be a personal number. If yes skip it. We do not want inconsistencies.
      // this happens when a lodas number would be given and the personal number matching this is further behind in the persons array
      do {
        currentLodasPersonalNr = availableLodasNumbers.at(index);
        if (!currentLodasPersonalNr) throw new Error('No more available PersonalNr');
        index++;
      } while (personalNumbers.includes(currentLodasPersonalNr));

      availableLodasNumbers.splice(0, index);

      newNumber = currentLodasPersonalNr.toString();
    }

    person.lodasPersonalNr = newNumber;
    await personCol.updateOne(
      { _id: person.mitarbeiterID },
      { $set: { 'mitarbeiterDaten.lodasPersonalNr': person.lodasPersonalNr } },
    );
  }

  return persons;
}

export const exportLodasDaten = registerQuery({
  operationName: 'exportLodasDaten',
  middlewares: [checkPermissionMiddleware(Permission.LODAS_EXPORT, ANY_CONTEXT)],
  resolve: (listSelection: UserListSelectionInterface, config: LodasConfiguration) => {
    return async () => {
      const lodasTablesInformation: LodasTableDaten[] = [
        {
          order: '1',
          name: 'u_lod_psd_mitarbeiter',
          fields: generateLodasTableFields(ULodPSDMitarbeiterTableEnum),
          active: true,
        },
        {
          order: '2',
          name: 'u_lod_psd_ma_bank',
          fields: generateLodasTableFields(ULodPsdMABankTableEnum),
          active: true,
        },
        {
          order: '3',
          name: 'u_lod_psd_steuer',
          fields: generateLodasTableFields(ULodPsdSteuerTableEnum),
          active: config.steuerdaten,
        },

        {
          order: '4',
          name: 'u_lod_psd_taetigkeit',
          fields: generateLodasTableFields(ULodPsdTaetigkeitTableEnum),
          active: true,
        },
        {
          order: '5',
          name: 'u_lod_psd_sozialversicherung',
          fields: generateLodasTableFields(ULodPsdSozialversicherungTableEnum),
          active: true,
        },
        {
          order: '6',
          name: 'u_lod_psd_priv_versicherung',
          fields: generateLodasTableFields(ULodPsdPrivVersicherungTableEnum),
          active: true,
        },
        {
          order: '7',
          name: 'u_lod_psd_beschaeftigung',
          fields: generateLodasTableFields(ULodPsdBeschaeftigungTableEnum),
          active: true,
        },
        {
          order: '8',
          name: 'u_lod_psd_arbeitszeit_regelm',
          fields: generateLodasTableFields(ULodPsdArbeitszeitRegelmTableEnum),
          active: true,
        },
        {
          order: '9',
          name: 'u_lod_psd_freibetrag',
          fields: generateLodasTableFields(ULodPsdFreibetragTableEnum),
          active: true,
        },
        {
          order: '10',
          name: 'u_lod_psd_vermoegensbildung',
          fields: generateLodasTableFields(ULodPsdVermoegensbildungTableEnum),
          active: true,
        },
        {
          order: '11',
          name: 'u_lod_psd_besonderheiten',
          fields: generateLodasTableFields(ULodPsdBesonderheitenTableEnum),
          active: true,
        },
        // {
        //   order: '11',
        //   name: 'u_lod_psd_oed_entgelt',
        //   fields: generateLodasTableFields(ULodPsdOedTableEnum),
        // },
      ];

      let satzbeschreibungDetail = '';
      let dataKostenstellen = '';
      let bezuegeData = '';
      let childData = '';
      let fehlZeitenData = '';
      let nettoData = '';
      let sonderzahlungen = '';

      const count = await resolveGetAllLodasApplicableMitarbeiter(listSelection, true, -1)({});

      let left = count as number;
      let skip = 0;
      while (left > 0) {
        let personen = await getAllLodasApplicableMitarbeiter(listSelection, false, skip)(LODAS_SELECTIONSET);

        if (!Array.isArray(personen)) return;

        // isContractActive takes a plain Date and normalizes it to its German calendar day itself
        personen = personen.filter(p => (p.activeContract ? isContractActive(new Date(), p.activeContract) : false));

        if (!personen.length) {
          skip = skip + 20;
          left -= 20;
          continue;
        }

        logger.warn('For persons ' + personen.length);

        const convertedRawLodasData = convertMartinusPersonToLodasPersonData(personen as Person[]);
        const convertedLodasData = await convertAndFixPersonalNUmberToLodasFormat(convertedRawLodasData);

        const satzbeschreibungDetailBatch = await generateStammDaten(lodasTablesInformation, convertedLodasData);
        const dataKostenstellenBatch = generateKostenStellen('1000', convertedLodasData);

        const salary = await getSalaryTable();
        const bezuegeDataBatch = await generateSalary('1003', convertedLodasData, false, salary);
        const childDataBatch = generateChildData('1004', convertedLodasData);
        const fehlZeitenDataBatch = generateFehlzeitenData('1005', convertedLodasData);
        const nettoDataBatch = await generateSalary('1006', convertedLodasData, true, salary);
        const sonderzahlungenBatch = generateSonderzahlungen('2000', convertedLodasData);

        satzbeschreibungDetail = satzbeschreibungDetail + satzbeschreibungDetailBatch + '\n';
        dataKostenstellen = dataKostenstellen + dataKostenstellenBatch + '\n';
        bezuegeData = bezuegeData + bezuegeDataBatch + '\n';
        childData = childData + childDataBatch + '\n';
        fehlZeitenData = fehlZeitenData + fehlZeitenDataBatch + '\n';
        nettoData = nettoData + nettoDataBatch + '\n';
        sonderzahlungen = sonderzahlungen + sonderzahlungenBatch + '\n';

        skip = skip + 20;
        left -= 20;
      }

      const lohnartData = await generateLohnartLodasData('1002');
      const krankenkassenData = await generateKrankenkassenLodasData('1001');

      const strSatzbeschreibung = generateSatzbeschreibung(lodasTablesInformation);
      const today = todayAsCalendarDay();
      const firstOfMonth = startOfMonth(today);
      const strResult =
        config.type === 'stammdaten'
          ? [
              '[Allgemein]',
              'Ziel=Lodas',
              'Version_SST=1.0',
              'Version_DB=12.72',
              'BeraterNr=1003811',
              'MandantenNr=18888',
              'Feldtrennzeichen=;',
              'Zahlenkomma=,',
              'Datumsformat=TT.MM.JJJJ',
              'Stringbegrenzer=""',
              'Kommentarzeichen=*',
              'StammdatenGueltigAb=' + formatCalendarDay(firstOfMonth, 'dd.MM.yyyy'),
              'BetrieblichePNrVerwenden=Ja',
              '',
              '[Satzbeschreibung]',
              strSatzbeschreibung,
              '1000;u_lod_psd_kstellen_verteil;pnr_betriebliche#psd;pnr#psd;kostenstelle#psd;prozentsatz_kst#psd',
              config.krankenkassen
                ? '1001;u_lod_mpd_krankenkasse;krankenkasse_nr#mpd;kk_betr_nr#mpd;krankenkasse_bezeichnung#mpd;'
                : '',
              config.tariftabelle ? '1002;u_lod_mpd_lohnart;la_eigene#mpd;la_bezeichnung#mpd;stla#mpd;' : '',
              '1003;u_lod_psd_festbezuege;pnr_betriebliche#psd;pnr#psd;festbez_id#psd;lohnart_nr#psd;betrag#psd',
              '1004;u_lod_psd_kindergeld;pnr_betriebliche#psd;pnr#psd;kind_nr#psd;kind_geburtsdatum#psd;kind_nachname#psd;kind_vorname#psd',
              '1005;u_lod_psd_fehlzeiten;pnr_betriebliche#psd;pnr#psd;datum_von_ttmmjjjj#psd;datum_bis_ttmmjjjj#psd;grund_fehlzeiten#psd',
              '1006;u_lod_psd_nettobeabzug;pnr_betriebliche#psd;pnr#psd;nettobeabzug_id#psd;nba_nr#psd;betrag#psd',

              '[Stammdaten]',
              satzbeschreibungDetail,
              dataKostenstellen,
              config.krankenkassen ? krankenkassenData : '',
              config.tariftabelle ? lohnartData : '',
              bezuegeData,
              childData,
              fehlZeitenData,
              nettoData,
              '',
              '[Bewegungsdaten]',
              '',
            ].join('\r\n')
          : [
              '[Allgemein]',
              'Ziel=Lodas',
              'Version_SST=1.0',
              'Version_DB=12.72',
              'BeraterNr=1003811',
              'MandantenNr=18888',
              'Feldtrennzeichen=;',
              'Zahlenkomma=,',
              'Datumsformat=TT.MM.JJJJ',
              'Stringbegrenzer=""',
              'Kommentarzeichen=*',
              'StammdatenGueltigAb=' + formatCalendarDay(firstOfMonth, 'dd.MM.yyyy'),
              'BetrieblichePNrVerwenden=Nein',
              '',
              '[Satzbeschreibung]',
              '2000;u_lod_bwd_buchung_standard;abrechnung_zeitraum#bwd;pnr#bwd;bs_wert_butab#bwd;bs_nr#bwd;kostenstelle#bwd;la_eigene#bwd',
              '',
              '[Stammdaten]',
              '',
              '[Bewegungsdaten]',
              sonderzahlungen,
            ].join('\r\n');

      const lodasTxtFile = iconv.encode(strResult, 'utf-8', { addBOM: true });
      const filename = ExpressEndpointFilenames.lodas;
      await writeFile(filename, lodasTxtFile);
      logger.warn({ filename }, `Created Lodas-TXT file.`);
      return A.absoluteUrl(ExpressEndpointURLs.lodas);
    };
  },
  type: () => String,
  params: [
    { type: () => UserListSelectionInterface, name: 'listSelection' },
    { type: () => LodasConfiguration, name: 'config' },
  ],
});
